import { get } from '@/lib/api';
import { formatTimestamp } from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import type { ActionInventoryDetail, InventoryMetadataField } from '@uncain/shared';
import { Link, useParams } from 'react-router';

export function InventoryActionDetailPage() {
	const params = useParams();
	const actionId = decodeURIComponent(params['*'] ?? '');
	const { data, isLoading, error } = useQuery<ActionInventoryDetail>({
		queryKey: queryKeys.inventory.actionDetail(actionId),
		queryFn: () => get<ActionInventoryDetail>(`/inventory/actions/${encodeURIComponent(actionId)}`),
		enabled: actionId.length > 0,
	});

	if (isLoading) {
		return <div className="text-muted-foreground">Loading action detail...</div>;
	}

	if (error || !data) {
		return <div className="text-destructive">Failed to load action detail.</div>;
	}

	const { action, versions, usages } = data;

	return (
		<div className="space-y-6">
			<Link to="/inventory/actions" className="text-sm text-primary hover:underline">
				&larr; Back to Actions
			</Link>

			<div className="space-y-2">
				<p className="text-sm uppercase tracking-[0.18em] text-muted-foreground">Inventory</p>
				<h1 className="text-2xl font-bold text-foreground">{action.displayName}</h1>
				<p className="text-sm text-muted-foreground">
					Used in {action.repositoryCount} repositories and {action.workflowCount} workflows in the
					active organization.
				</p>
			</div>

			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				<SummaryCard label="Usages" value={String(action.usageCount)} />
				<SummaryCard label="Repositories" value={String(action.repositoryCount)} />
				<SummaryCard label="Workflows" value={String(action.workflowCount)} />
				<SummaryCard label="Versions" value={String(action.versionCount)} />
			</div>

			<section className="rounded-xl border border-border bg-card p-5">
				<h2 className="text-lg font-semibold text-foreground">Metadata</h2>
				<div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
					<MetadataField
						label="Last Ran"
						field={data.lastRanAt}
						renderValue={(value) =>
							typeof value === 'number' ? formatTimestamp(value) : 'Unavailable'
						}
					/>
					<MetadataField label="Risk Score" field={data.riskScore} />
					<MetadataField
						label="Latest Version"
						field={{
							status: action.latestVersionRef ? 'available' : 'unavailable',
							value: action.latestVersionRef,
							note: action.latestVersionRef ? null : 'No version information is available.',
						}}
					/>
					<MetadataField
						label="Latest Resolved"
						field={{
							status: action.latestResolvedAt ? 'available' : 'unavailable',
							value: action.latestResolvedAt,
							note: action.latestResolvedAt ? null : 'No resolved timestamp is available.',
						}}
						renderValue={(value) =>
							typeof value === 'number' ? formatTimestamp(value) : 'Unavailable'
						}
					/>
				</div>
			</section>

			<section className="rounded-xl border border-border bg-card p-5">
				<h2 className="text-lg font-semibold text-foreground">Versions</h2>
				<div className="mt-4 space-y-3">
					{versions.map((version) => (
						<div
							key={version.id}
							className="rounded-lg border border-border/70 bg-background/50 p-4"
						>
							<div className="flex flex-wrap items-center gap-2">
								<span className="font-medium text-foreground">
									{version.ref} · {version.resolutionClass}
								</span>
								{version.isQuarantined && (
									<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
										Quarantined
									</span>
								)}
							</div>
							<p className="mt-1 break-all font-mono text-xs text-muted-foreground">
								{version.resolvedSha}
							</p>
							<p className="mt-2 text-sm text-muted-foreground">
								{version.usageCount} usages · {version.repositoryCount} repos ·{' '}
								{version.workflowCount} workflows
							</p>
						</div>
					))}
				</div>
			</section>

			<section className="rounded-xl border border-border bg-card p-5">
				<h2 className="text-lg font-semibold text-foreground">Usage</h2>
				<div className="mt-4 space-y-3">
					{usages.map((usage) => (
						<div
							key={`${usage.workflowId}-${usage.jobId ?? 'workflow'}-${usage.versionId}`}
							className="rounded-lg border border-border/70 bg-background/50 p-4"
						>
							<div className="flex flex-wrap items-center gap-2">
								<span className="font-medium text-foreground">{usage.repositoryFullName}</span>
								<span className="text-sm text-muted-foreground">{usage.workflowPath}</span>
								{usage.isReleasePath && (
									<span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
										Release path
									</span>
								)}
								{usage.findingCount > 0 && (
									<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
										{usage.findingCount} finding{usage.findingCount === 1 ? '' : 's'}
									</span>
								)}
							</div>
							<p className="mt-1 text-sm text-muted-foreground">
								{usage.workflowName}
								{usage.jobKey ? ` · job ${usage.jobKey}` : ''}
								{` · ${usage.edgeType}`}
							</p>
							<p className="mt-2 break-all font-mono text-xs text-muted-foreground">
								{usage.ref} → {usage.resolvedSha}
							</p>
						</div>
					))}
				</div>
			</section>
		</div>
	);
}

function SummaryCard({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-border bg-card p-4">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
		</div>
	);
}

function MetadataField<T>({
	label,
	field,
	renderValue,
}: {
	label: string;
	field: InventoryMetadataField<T>;
	renderValue?: (value: T | null) => string;
}) {
	const displayValue =
		field.status === 'available'
			? renderValue
				? renderValue(field.value)
				: String(field.value ?? '')
			: 'Unavailable';

	return (
		<div className="rounded-lg border border-border/70 bg-background/50 p-4">
			<p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
			<p
				className={cn(
					'mt-2 text-sm font-medium',
					field.status === 'available' ? 'text-foreground' : 'text-muted-foreground',
				)}
			>
				{displayValue}
			</p>
			{field.note && <p className="mt-2 text-xs text-muted-foreground">{field.note}</p>}
		</div>
	);
}
